/**
 * Formats a date string to a more readable format in Slovenian
 * @param dateString - The date string to format
 * @returns Formatted date string
 */
export const formatDate = (dateString: string): string => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  
  // Return formatted date in Slovenian format
  return date.toLocaleDateString('sl-SI', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

/**
 * Truncates text to a specific length and adds ellipsis
 * @param text - The text to truncate
 * @param maxLength - The maximum length before truncation
 * @returns Truncated text with ellipsis if needed
 */
export const truncateText = (text: string, maxLength: number = 150): string => {
  if (!text || text.length <= maxLength) return text;
  
  // Truncate at the last space before maxLength to avoid cutting words
  const truncated = text.substring(0, maxLength);
  const lastSpace = truncated.lastIndexOf(' ');
  
  return `${truncated.substring(0, lastSpace || maxLength)}...`;
};

/**
 * Slugifies a string for use in URLs
 * @param text - The text to slugify
 * @returns Slugified text
 */
export const slugify = (text: string): string => {
  if (!text) return '';
  
  // Replace Slovenian characters with their ASCII equivalents
  const withoutDiacritics = text
    .replace(/č/g, 'c')
    .replace(/š/g, 's')
    .replace(/ž/g, 'z')
    .replace(/Č/g, 'C')
    .replace(/Š/g, 'S')
    .replace(/Ž/g, 'Z');
  
  // Convert to lowercase, replace spaces with hyphens, remove special characters
  return withoutDiacritics
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^\w\-]+/g, '')
    .replace(/\-\-+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '');
};

/**
 * Formats a price string to ensure it has the Euro symbol
 * @param price - The price string to format
 * @returns Formatted price string
 */
export const formatPrice = (price?: string): string => {
  if (!price) return '';
  
  // If price already has Euro symbol, return as is
  if (price.includes('€')) return price;
  
  // Otherwise add Euro symbol
  return `${price} €`;
};